import { Component, OnInit, HostListener } from '@angular/core';
import {
  IonIcon, IonItem, IonLabel, IonInput, IonList, IonChip,
  IonToolbar, IonTitle, IonButtons, IonButton, IonSpinner,
  ModalController
} from '@ionic/angular/standalone';
import { NgClass } from '@angular/common';
import { TranslocoModule, TranslocoService, provideTranslocoScope } from '@jsverse/transloco';
import { format, parse, isAfter, startOfDay } from 'date-fns';
import { isBefore, isEqual } from 'date-fns';
import { finalize, switchMap, takeUntil, map } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { LoaderService } from '../../../services/loader.service';
import { NotificationService } from '../../../services/notification.service';
import { GlobalService, MensaRefreshReason } from '../../../services/global.service';
import {
  StudenteCittadinoDettaglioService,
  ServizioMensaEventoCalendarioDTO, ServizioMensaEventoCalendarioForm, ServizioMensaEventoCalendarioType,
  ServizioMensaAddPatchRicaricaForm,
  ServizioMensaRicaricaDTO, ServizioMensaRicaricaStato,
  ConfigurazioneMensaCittadinoService,
  ServizioTipoPagamentoEnum as TipoPagamentoEnum,
  CittadinoServizioMensaConfigurazioneDTOTipoPagamentoEnum as ConfigTipoPagamentoEnum
} from '../../../core/api/generated';
import { Studente } from '../../interfaces/studente-figlio.interface';
import { parseImportoLocale } from '../../utilities/funzioni-stringhe';
import { ServiziAttiviService } from '../../../services/pratiche/servizi-attivi.service';
import { StudentiMensaService } from '../../../features/cittadino/mensa/studenti/studenti-mensa.service';
import { mapDiscoveryToStudente } from '../../../features/cittadino/mensa/studenti/map-discovery-studente';
import { CommandAction } from '../../interfaces/command-action.interface';
import { ModalRoleEnum } from '../../enums/modal-enum';
import { CalendarActionFormComponent } from './calendar-action-form/calendar-action-form.component';
import { CommandParserService } from '../../services/command-parser/command-parser.service';
import { ParseResult, ParsedToken, INTENT_LABELS, isRemoveAction, isPaymentAction, isNavigationAction, formatEuro } from '../../services/command-parser/command-parser.model';
import { CurrencyEuroPipe } from '../../pipes/currency-euro.pipe';
import { PathEnum } from '../../enums/path-enum';
import { formatDateForDisplay } from '../../services/command-parser/date-parser';
import {
  getExcludedDays, isExcludedDay, getEventState, isChiusura, getSospesoKey, loadEventiEChiusure$
} from '../../services/command-parser/calendar-validators';
import { UnsubscribeOnDestroy } from '../../abstracts/unsubscribe-on-destroy';


type PaletteStep = 'search' | 'select-child' | 'action-form' | 'amount-form' | 'remove-form' | 'confirm';

export interface RemoveChip {
  evento: ServizioMensaEventoCalendarioDTO;
  label: string;
  shortDate: string;
}

// `labelKey`: chiave i18n della label visibile (tradotta a render-time).
// `command`: testo italiano iniettato nel parser NLU al click — INVARIANTE per lingua,
// perché il parser riconosce solo keyword italiane (vedi applyExample).
const EXAMPLE_CHIPS: { labelKey: string; command: string; icon: string }[] = [
  { labelKey: 'shared.command_palette.examples.assenzaDomani', command: 'assenza domani', icon: 'calendar-outline' },
  { labelKey: 'shared.command_palette.examples.pastaBiancoLunedi', command: 'pasta bianco lunedì', icon: 'restaurant-outline' },
];

// `labelKey`: chiave i18n della label azione (risolta a render-time nel template).
// Le `keywords` restano italiane: sono token tecnici di matching del parser.
const CALENDAR_ACTIONS: CommandAction[] = [
  { id: 'assenza', label: 'shared.command_palette.actions.aggiungiAssenza', icon: 'calendar-outline', actionType: 'assenza',
    keywords: ['assente', 'malato', 'malattia', 'mancanza', 'non presente', 'giustifica', 'calendario'] },
  { id: 'pasta', label: 'shared.command_palette.actions.aggiungiPasta', icon: 'restaurant-outline', actionType: 'pasta-in-bianco',
    keywords: ['bianco', 'dieta', 'leggera', 'menu', 'pasto', 'speciale', 'calendario'] },
  { id: 'rimuovi-assenza', label: 'shared.command_palette.actions.rimuoviAssenza', icon: 'calendar-clear-outline', actionType: 'rimuovi-assenza',
    keywords: ['rimuovi', 'cancella', 'annulla', 'elimina', 'togli', 'calendario'] },
  { id: 'rimuovi-pasta', label: 'shared.command_palette.actions.rimuoviPasta', icon: 'close-circle-outline', actionType: 'rimuovi-pasta-in-bianco',
    keywords: ['rimuovi', 'cancella', 'annulla', 'elimina', 'togli', 'calendario'] },
];

const PAYMENT_ACTION: CommandAction = {
  id: 'genera-pagamento', label: 'shared.command_palette.actions.generaPagamento', icon: 'card-outline', actionType: 'genera-pagamento',
  keywords: ['ricarica', 'ricariche', 'pagamento', 'pagamenti', 'bollettino', 'pagopa', 'euro']
};

const NAVIGATION_PAYMENT_ACTION: CommandAction = {
  id: 'vai-pagamenti', label: 'shared.command_palette.actions.vaiPagamenti', icon: 'wallet-outline', actionType: 'navigazione-pagamenti',
  keywords: ['pagamenti', 'ricariche', 'bollettino', 'pagopa']
};

/**
 * Visibilità dell'azione "genera pagamento/ricarica": driver SINCRONO sul solo tipo pagamento
 * della riga (`servizio.tipo_pagamento`, già in discovery). Il gate `gestione_manuale` (stato
 * manuale ATTIVA) NON è nella discovery: è verificato async in `runPaymentValidation` al confirm.
 * Degradazione coerente: il tipo decide la visibilità, lo stato manuale l'abilitazione.
 */
function studenteHasRicaricheAttive(s: Studente): boolean {
  const tipo = s.tipoPagamento;
  return tipo === TipoPagamentoEnum.PAGO_PA_RICARICHE
    || tipo === TipoPagamentoEnum.PAGO_PA_RENDICONTAZIONE_E_RICARICHE;
}

function studenteHasPagoPa(s: Studente): boolean {
  const tipo = s.tipoPagamento;
  return !!tipo && tipo !== TipoPagamentoEnum.NON_GESTITO && tipo !== TipoPagamentoEnum.MANUALE;
}

function buildActions(studenti: Studente[]): CommandAction[] {
  const actions: CommandAction[] = [...CALENDAR_ACTIONS];
  if (studenti.some(studenteHasRicaricheAttive)) {
    actions.push(PAYMENT_ACTION);
  }
  if (studenti.some(studenteHasPagoPa)) {
    actions.push(NAVIGATION_PAYMENT_ACTION);
  }
  return actions;
}

@Component({
  selector: 'app-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
  standalone: true,
  imports: [
    IonIcon, IonItem, IonLabel, IonInput, IonList, IonChip,
    IonToolbar, IonTitle, IonButtons, IonButton, IonSpinner,
    NgClass, CalendarActionFormComponent, CurrencyEuroPipe, TranslocoModule
  ],
  providers: [provideTranslocoScope('shared')]
})
export class CommandPaletteComponent extends UnsubscribeOnDestroy implements OnInit {
  readonly EXAMPLE_CHIPS = EXAMPLE_CHIPS;

  step: PaletteStep = 'search';
  searchText = '';
  availableActions: CommandAction[] = [];
  filteredActions: CommandAction[] = [];
  selectedAction: CommandAction | null = null;
  activeIndex = -1;

  studenti: Studente[] = [];
  selectedStudente: Studente | null = null;
  /** `true` finché la discovery viva non ha emesso la prima lista (caricamento lazy). */
  studentiLoading = true;

  // NLP parser state
  parseResult: ParseResult | null = null;
  prefilledDateInizio: string | null = null;
  prefilledDateFine: string | null = null;
  private nluDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  nluLoading = false;

  // Remove-form step state
  removeChips: RemoveChip[] = [];
  selectedRemoveChip: RemoveChip | null = null;
  loadingRemoveEvents = false;

  // Amount-form step state
  amountInput: number | null = null;
  residuoHint: number | null = null;

  // Confirm step state
  confirmSaving = false;
  loadingValidation = false;
  confirmWarnings: string[] = [];
  canConfirm = false;
  confirmRemoveEventId: string | null = null;

  constructor(
    private modalController: ModalController,
    private parser: CommandParserService,
    private studentiService: StudenteCittadinoDettaglioService,
    private loaderService: LoaderService,
    private notificationService: NotificationService,
    private globalService: GlobalService,
    private router: Router,
    private transloco: TranslocoService,
    private serviziAttivi: ServiziAttiviService,
    private studentiMensa: StudentiMensaService,
    private configMensa: ConfigurazioneMensaCittadinoService
  ) {
    super();
  }

  /**
   * Id dell'istanza mensa attiva = header `Gestione-Mense-Servizio-Id` per le API `*2`. La palette
   * è globale (figlio scelto nel modale), quindi NON usa `activeMealContext$` (per-pagina): la
   * sorgente è `ServiziAttiviService.idServizioMensaAttivo$()`. `null` se nessun servizio mensa attivo.
   */
  private idServizioMensa(): Promise<string | null> {
    return this.serviziAttivi.idServizioMensaAttivo();
  }

  /**
   * Classifica il motivo di refresh per un'azione su evento calendario (vedi capability
   * `mensa-refresh-cross-area`): `evento-retroattivo` SOLO per un'ASSENZA che tocca almeno un
   * giorno passato (il BE ricalcola portafoglio+rendicontazione); tutto il resto — pasta in
   * bianco (anche retroattiva) e assenze future — è `evento`. Il criterio "giorno passato" è
   * `isBefore(startOfDay(d), startOfDay(oggi))`, allineato a `eventi-mensa.toccaGiornoPassato`.
   */
  private classifyEventoReason(
    actionType: string | null,
    inizio: Date,
    fine?: Date | null,
  ): MensaRefreshReason {
    if (actionType !== 'assenza' && actionType !== 'rimuovi-assenza') {
      return 'evento';
    }
    const oggi = startOfDay(new Date());
    const toccaPassato = isBefore(startOfDay(inizio), oggi)
      || (!!fine && isBefore(startOfDay(fine), oggi));
    return toccaPassato ? 'evento-retroattivo' : 'evento';
  }

  private toastNoServizio(): void {
    this.notificationService.openToast({
      message: this.transloco.translate('shared.command_palette.toast.erroreSalvataggio'),
      color: 'danger'
    });
  }

  override ngOnDestroy() {
    if (this.nluDebounceTimer) {
      clearTimeout(this.nluDebounceTimer);
    }
    super.ngOnDestroy();
  }

  ngOnInit() {
    // La lista NON è uno snapshot sincrono: `StudentiMensaService.studenti$` si carica in modo
    // lazy alla prima sottoscrizione, quindi il getter sincrono sarebbe `undefined` al primo
    // render (era la causa del bug "select-child a chip vuote"). Ci si sottoscrive e si popola
    // all'emissione. Vedi change `repoint-command-palette-discovery` (D1/D4).
    this.studentiMensa.studenti$.pipe(takeUntil(this.destroy$)).subscribe((righe) => {
      if (!righe) {
        // `undefined` = non ancora caricata: resta in loading.
        return;
      }
      this.studenti = righe.map(mapDiscoveryToStudente);
      this.availableActions = buildActions(this.studenti);
      this.filteredActions = [...this.availableActions];
      this.studentiLoading = false;

      // Default selezione: unico studente → auto; altrimenti riga già selezionata nella discovery.
      if (this.studenti.length === 1) {
        this.selectedStudente = this.studenti[0];
      } else {
        const selId = this.studentiMensa.selezionato?.id;
        this.selectedStudente = selId
          ? this.studenti.find((s) => s.id === selId) ?? null
          : null;
      }
    });
  }

  // --- Search step ---

  get resultsAnnouncement(): string {
    const count = this.filteredActions.length;
    if (count === 0) {
      return this.transloco.translate('shared.command_palette.announce.nessunaAzione');
    }
    const key = count === 1
      ? 'shared.command_palette.announce.azioniDisponibili.one'
      : 'shared.command_palette.announce.azioniDisponibili.other';
    return this.transloco.translate(key, { count });
  }

  /** Etichetta i18n del tipo di token (mappa risolta a render-time). */
  private tokenTypeLabel(type: string): string {
    const keys: Record<string, string> = {
      intent: 'shared.command_palette.announce.tokenType.azione',
      figlio: 'shared.command_palette.announce.tokenType.studente',
      data: 'shared.command_palette.announce.tokenType.data',
      importo: 'shared.command_palette.announce.tokenType.importo',
    };
    return keys[type] ? this.transloco.translate(keys[type]) : type;
  }

  get previewAnnouncement(): string {
    if (!this.parseResult || this.parseResult.confidence === 'none') return '';
    const parts: string[] = [];
    for (const token of this.parseResult.tokens) {
      if (token.type === 'noise') continue;
      parts.push(this.transloco.translate('shared.command_palette.announce.tokenLine', {
        tipo: this.tokenTypeLabel(token.type),
        valore: token.resolved,
      }));
    }
    if (this.parseResult.confidence === 'full') {
      parts.push(this.transloco.translate('shared.command_palette.announce.premiInvio'));
    }
    return parts.join('. ');
  }

  private static readonly REMOVE_VERBS = new Set([
    'rimuovi', 'cancella', 'togli', 'annulla', 'elimina',
  ]);

  get missingSlotHint(): string | null {
    if (!this.parseResult) return null;

    // When no intent is detected, check if user typed a remove verb without object
    if (this.parseResult.confidence === 'none' || (!this.parseResult.intent && this.parseResult.confidence === 'partial')) {
      const noiseTokens = this.parseResult.tokens
        .filter(t => t.type === 'noise')
        .map(t => t.raw.toLowerCase());
      if (noiseTokens.some(t => CommandPaletteComponent.REMOVE_VERBS.has(t))) {
        return this.transloco.translate('shared.command_palette.hint.specificaCosa');
      }
      return null;
    }

    if (this.parseResult.confidence !== 'partial') return null;
    if (!this.parseResult.intent) return null;

    const { intent, figlio, data, importo } = this.parseResult;
    const needsChild = !figlio && this.studenti.length > 1;
    const childExample = this.studenti.length > 0
      ? `"${this.studenti[0].nome}"`
      : `"${this.transloco.translate('shared.command_palette.hint.nomeEsempio')}"`;
    const hintChi = () => this.transloco.translate('shared.command_palette.hint.aggiungiChi', { esempio: childExample });

    if (isPaymentAction(intent)) {
      if (needsChild) return hintChi();
      if (importo === null) return this.transloco.translate('shared.command_palette.hint.aggiungiImporto');
      return null;
    }

    if (isNavigationAction(intent)) return null;

    if (isRemoveAction(intent)) {
      if (needsChild) return hintChi();
      return null;
    }

    // assenza, pasta-in-bianco
    if (!data) return this.transloco.translate('shared.command_palette.hint.aggiungiQuando');
    if (needsChild) return hintChi();
    return null;
  }

  applyExample(command: string): void {
    this.searchText = command;
    this.onSearchChange(command);
  }

  onSearchChange(value: string) {
    this.searchText = value;

    // Level 1: keyword parse (sync, instant)
    this.parseResult = this.parser.parseLive(value, this.studenti);

    if (this.parseResult.intent) {
      this.nluLoading = false;
      this.filteredActions = this.availableActions.filter(a =>
        a.actionType === this.parseResult!.intent
      );
    } else {
      const term = value.toLowerCase();
      this.filteredActions = this.availableActions.filter(a =>
        this.transloco.translate(a.label).toLowerCase().includes(term) ||
        a.keywords?.some(k => k.includes(term))
      );

      // Level 2: NLU fallback with 300ms debounce
      this.scheduleNluLive(value, this.parseResult);
    }
    this.activeIndex = -1;
  }

  private scheduleNluLive(value: string, currentResult: ParseResult) {
    if (this.nluDebounceTimer) {
      clearTimeout(this.nluDebounceTimer);
    }
    if (!value || !value.trim()) {
      this.nluLoading = false;
      return;
    }

    this.nluLoading = true;
    this.nluDebounceTimer = setTimeout(async () => {
      if (this.step !== 'search' || this.searchText !== value) {
        this.nluLoading = false;
        return;
      }

      const matched = await this.parser.tryNluFallback(currentResult);
      this.nluLoading = false;

      if (!matched) return;

      // Still on same input?
      if (this.step !== 'search' || this.searchText !== value) return;

      this.parseResult = currentResult;
      this.filteredActions = this.availableActions.filter(a => a.actionType === currentResult.intent);
    }, 300);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (this.step === 'search') {
      this.handleSearchKeyboard(event);
    }
  }

  private handleSearchKeyboard(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.filteredActions.length - 1);
        if (this.activeIndex < 0) this.activeIndex = 0;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        break;
      case 'Enter':
        event.preventDefault();
        this.handleEnter();
        break;
    }
  }

  async handleEnter() {
    if (!this.searchText?.trim()) return;

    const result = await this.parser.parse(this.searchText, this.studenti);

    if (isNavigationAction(result.intent)) {
      this.navigateToPayments();
      return;
    }

    if (result.confidence === 'full') {
      this.parseResult = result;
      this.goToConfirm(result);
    } else if (result.intent) {
      this.parseResult = result;
      this.selectActionFromParse(result);
    } else if (this.filteredActions[this.activeIndex]) {
      this.selectAction(this.filteredActions[this.activeIndex]);
    } else {
      this.notificationService.openToast({
        message: this.transloco.translate('shared.command_palette.toast.nonCapito'),
        color: 'warning'
      });
    }
  }

  private goToConfirm(result: ParseResult) {
    this.selectedAction = this.availableActions.find(a => a.actionType === result.intent) ?? null;
    this.selectedStudente = result.figlio;
    this.confirmWarnings = [];
    this.canConfirm = false;
    this.loadingValidation = false;
    this.confirmRemoveEventId = null;

    this.step = 'confirm';
    if (isRemoveAction(result.intent)) {
      this.runRemoveConfirmValidation(result);
    } else if (isPaymentAction(result.intent)) {
      this.runPaymentValidation();
    } else {
      this.runConfirmValidation();
    }
  }

  private runConfirmValidation() {
    const warnings: string[] = [];

    // 1. Synchronous checks
    const sospesoKey = getSospesoKey(this.selectedStudente);
    if (sospesoKey) {
      warnings.push(this.transloco.translate(sospesoKey));
      this.confirmWarnings = warnings;
      this.canConfirm = false;
      return; // Don't call API if suspended
    }

    if (this.parseResult?.data) {
      const data = this.parseResult.data;

      // Inizio > fine
      if (data.fine && isBefore(data.fine, data.inizio) && !isEqual(data.fine, data.inizio)) {
        warnings.push(this.transloco.translate('shared.command_palette.validation.inizioDopoFine'));
        this.confirmWarnings = warnings;
        this.canConfirm = false;
        return;
      }

      // Weekly closure days
      const excludedDays = getExcludedDays(this.selectedStudente);
      if (isExcludedDay(data.inizio, excludedDays)) {
        warnings.push(this.transloco.translate('shared.command_palette.validation.inizioChiusura'));
      }
      if (data.fine && !isEqual(data.fine, data.inizio) && isExcludedDay(data.fine, excludedDays)) {
        warnings.push(this.transloco.translate('shared.command_palette.validation.fineChiusura'));
      }

      if (warnings.length > 0) {
        this.confirmWarnings = warnings;
        this.canConfirm = false;
        return;
      }
    }

    // 2. Async checks — load events + closures from API
    const studenteId = this.selectedStudente?.id;
    if (!studenteId || !this.parseResult?.data) {
      this.canConfirm = false;
      return;
    }

    this.loadingValidation = true;
    this.idServizioMensa().then(idServizio => {
      if (!idServizio) {
        this.loadingValidation = false;
        this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.erroreVerifica')];
        this.canConfirm = false;
        return;
      }
      loadEventiEChiusure$(this.studentiService, studenteId, idServizio).pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingValidation = false)
      ).subscribe({
        next: ({ eventi, chiusure }) => this.validateWithEvents(eventi, chiusure),
        error: () => {
          this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.erroreVerifica')];
          this.canConfirm = false;
        }
      });
    });
  }

  private runRemoveConfirmValidation(result: ParseResult) {
    const studenteId = this.selectedStudente?.id;
    if (!studenteId || !result.data) {
      this.canConfirm = false;
      return;
    }

    const targetType = result.intent === 'rimuovi-assenza'
      ? ServizioMensaEventoCalendarioType.ASSENZA
      : ServizioMensaEventoCalendarioType.PASTA_IN_BIANCO;

    const targetDateFmt = format(result.data.inizio, 'dd/MM/yyyy');

    this.loadingValidation = true;
    this.idServizioMensa().then(idServizio => {
      if (!idServizio) {
        this.loadingValidation = false;
        this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.erroreVerifica')];
        this.canConfirm = false;
        return;
      }
      this.studentiService.listEventiCalendarioStudenteCittadino({ idStudente: studenteId, gestioneMenseServizioId: idServizio }).pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingValidation = false)
      ).subscribe({
        next: (eventi) => {
          const match = eventi.find(e => e.tipo === targetType && e.data === targetDateFmt && e.id);
          if (match) {
            this.confirmRemoveEventId = match.id!;
            this.confirmWarnings = [];
            this.canConfirm = true;
          } else {
            const tipo = this.transloco.translate(result.intent === 'rimuovi-assenza'
              ? 'shared.command_palette.tipoEvento.assenza'
              : 'shared.command_palette.tipoEvento.pastaInBianco');
            this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.nessunoTrovato', { tipo, data: targetDateFmt })];
            this.canConfirm = false;
          }
        },
        error: () => {
          this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.erroreVerifica')];
          this.canConfirm = false;
        }
      });
    });
  }

  private validateWithEvents(eventi: ServizioMensaEventoCalendarioDTO[], chiusure: ReadonlySet<string>) {
    const warnings: string[] = [];
    const data = this.parseResult!.data!;
    const dataInizioFmt = format(data.inizio, 'dd/MM/yyyy');

    // Chiusura scolastica on start date
    if (isChiusura(dataInizioFmt, chiusure)) {
      warnings.push(this.transloco.translate('shared.command_palette.validation.chiusuraInizio'));
    }

    // Chiusura scolastica on end date (range)
    if (data.fine && !isEqual(data.fine, data.inizio)) {
      const dataFineFmt = format(data.fine, 'dd/MM/yyyy');
      if (isChiusura(dataFineFmt, chiusure)) {
        warnings.push(this.transloco.translate('shared.command_palette.validation.chiusuraFine'));
      }
    }

    // Already has event on start date
    const stateInizio = getEventState(dataInizioFmt, eventi);
    if (stateInizio === 'assenza') {
      warnings.push(this.transloco.translate('shared.command_palette.validation.assenzaPresente'));
    } else if (stateInizio === 'pasta-in-bianco') {
      warnings.push(this.transloco.translate('shared.command_palette.validation.pastaPresente'));
    }

    this.confirmWarnings = warnings;
    this.canConfirm = warnings.length === 0;
  }

  private selectActionFromParse(result: ParseResult) {
    this.selectedAction = this.availableActions.find(a => a.actionType === result.intent) ?? null;

    if (isNavigationAction(result.intent)) {
      this.navigateToPayments();
      return;
    }

    if (result.figlio) {
      this.selectedStudente = result.figlio;
    }

    // Pre-fill dates if available
    if (result.data) {
      this.prefilledDateInizio = format(result.data.inizio, 'yyyy-MM-dd');
      this.prefilledDateFine = result.data.fine
        ? format(result.data.fine, 'yyyy-MM-dd')
        : this.prefilledDateInizio;
    } else {
      this.prefilledDateInizio = null;
      this.prefilledDateFine = null;
    }

    // Pre-fill amount if available
    if (result.importo !== null) {
      this.amountInput = result.importo;
    }

    if (this.selectedStudente) {
      this.goToActionStep();
    } else if (this.studenti.length === 1) {
      this.selectedStudente = this.studenti[0];
      this.goToActionStep();
    } else {
      this.step = 'select-child';
    }
  }

  selectAction(action: CommandAction) {
    this.selectedAction = action;
    this.prefilledDateInizio = null;
    this.prefilledDateFine = null;
    this.amountInput = null;

    if (isNavigationAction(action.actionType)) {
      this.navigateToPayments();
      return;
    }

    // Difesa: senza studenti non si entra in `select-child` (era il body collassato del bug).
    if (this.studenti.length === 0) {
      return;
    }

    if (this.selectedStudente) {
      this.goToActionStep();
    } else if (this.studenti.length === 1) {
      this.selectedStudente = this.studenti[0];
      this.goToActionStep();
    } else {
      this.step = 'select-child';
    }
  }

  selectChild(studente: Studente) {
    if (isPaymentAction(this.selectedAction?.actionType ?? null) && !studenteHasRicaricheAttive(studente)) {
      this.notificationService.openToast({
        message: this.transloco.translate('shared.command_palette.toast.ricaricaNonAttiva', { nome: studente.nome }),
        color: 'warning'
      });
      return;
    }
    this.selectedStudente = studente;
    this.goToActionStep();
  }

  private goToActionStep() {
    if (isRemoveAction(this.selectedAction?.actionType ?? null)) {
      this.loadRemoveEvents();
    } else if (isPaymentAction(this.selectedAction?.actionType ?? null)) {
      if (this.amountInput !== null && this.amountInput > 0) {
        // Amount already extracted from text — go to confirm
        this.buildPaymentParseResult();
        this.goToConfirm(this.parseResult!);
      } else {
        this.prepareAmountForm();
        this.step = 'amount-form';
      }
    } else {
      this.step = 'action-form';
    }
  }

  // --- Confirm step ---

  /** Mappa intent → chiave i18n della label azione (risolta a render-time). */
  private static readonly INTENT_LABEL_KEYS: Record<string, string> = {
    'assenza': 'shared.command_palette.intent.assenza',
    'pasta-in-bianco': 'shared.command_palette.intent.pastaInBianco',
    'rimuovi-assenza': 'shared.command_palette.intent.rimuoviAssenza',
    'rimuovi-pasta-in-bianco': 'shared.command_palette.intent.rimuoviPasta',
    'genera-pagamento': 'shared.command_palette.intent.generaPagamento',
    'navigazione-pagamenti': 'shared.command_palette.intent.vaiPagamenti',
  };

  get confirmActionLabel(): string {
    if (!this.parseResult?.intent) return '';
    const key = CommandPaletteComponent.INTENT_LABEL_KEYS[this.parseResult.intent];
    return key ? this.transloco.translate(key) : INTENT_LABELS[this.parseResult.intent];
  }

  get confirmStudenteName(): string {
    return this.selectedStudente
      ? `${this.selectedStudente.nome ?? ''} ${this.selectedStudente.cognome ?? ''}`.trim()
      : '';
  }

  get confirmDateLabel(): string {
    if (!this.parseResult?.data) return '';
    const d = this.parseResult.data;
    if (d.fine) {
      return `${formatDateForDisplay(d.inizio)} - ${formatDateForDisplay(d.fine)}`;
    }
    return formatDateForDisplay(d.inizio);
  }

  get confirmAmountLabel(): string {
    if (this.parseResult?.importo == null) return '';
    return formatEuro(this.parseResult.importo);
  }

  get isPaymentConfirm(): boolean {
    return isPaymentAction(this.selectedAction?.actionType ?? null);
  }

  get confirmFuzzyHint(): string | null {
    if (!this.parseResult?.childMatch || this.parseResult.childMatch.status !== 'fuzzy') return null;
    return this.parseResult.childMatch.originalInput ?? null;
  }

  async confirmSave() {
    if (!this.canConfirm) return;
    if (!this.selectedAction || !this.selectedStudente || !this.parseResult?.data) return;
    if (this.confirmSaving) return;

    const idServizio = await this.idServizioMensa();
    if (!idServizio) {
      this.toastNoServizio();
      return;
    }

    const studenteId = this.selectedStudente.id!;
    const dataInizioFmt = format(this.parseResult.data.inizio, 'dd/MM/yyyy');
    const dataFineFmt = this.parseResult.data.fine
      ? format(this.parseResult.data.fine, 'dd/MM/yyyy')
      : dataInizioFmt;

    const tipoEvento = this.selectedAction.actionType === 'assenza'
      ? ServizioMensaEventoCalendarioType.ASSENZA
      : ServizioMensaEventoCalendarioType.PASTA_IN_BIANCO;

    const body: ServizioMensaEventoCalendarioForm = {
      data_inizio: dataInizioFmt,
      data_fine: dataFineFmt,
      tipo_evento: tipoEvento,
    };

    this.confirmSaving = true;
    this.loaderService.showLoaderObserve$().pipe(
      switchMap(() => this.studentiService.addEventoCalendarioStudenteCittadino({ idStudente: studenteId, gestioneMenseServizioId: idServizio, servizioMensaEventoCalendarioForm: body })),
      takeUntil(this.destroy$),
      finalize(() => {
        this.loaderService.hideLoader();
        this.confirmSaving = false;
      })
    ).subscribe({
      next: () => {
        const label = this.transloco.translate(this.selectedAction!.actionType === 'assenza'
          ? 'shared.command_palette.toast.assenzaSalvata'
          : 'shared.command_palette.toast.pastaSalvata');
        this.notificationService.openToast({ message: label, color: 'success' });
        const reason = this.classifyEventoReason(
          this.selectedAction!.actionType,
          this.parseResult!.data!.inizio,
          this.parseResult!.data!.fine,
        );
        this.globalService.emitMensaChanged(reason);
        this.modalController.dismiss(null, ModalRoleEnum.CONFIRM);
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.openToast({
          message: error.error?.message || this.transloco.translate('shared.command_palette.toast.erroreSalvataggio'),
          color: 'danger'
        });
      }
    });
  }

  // --- Amount-form step ---

  private prepareAmountForm(prefill?: number | null) {
    const residuoRaw = Number(this.selectedStudente?.portafogli?.residuo ?? 0);
    this.residuoHint = residuoRaw < 0 && isFinite(residuoRaw) ? Math.abs(residuoRaw) : null;
    this.amountInput = prefill ?? null;
  }

  onAmountChange(value: string) {
    // Parsing locale-aware condiviso: gestisce il separatore migliaia (`1.500` → 1500),
    // non solo la prima virgola. Coerente con `amount-parser`.
    const parsed = parseImportoLocale(value);
    this.amountInput = isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  confirmAmount() {
    if (!this.amountInput || this.amountInput <= 0) return;
    this.buildPaymentParseResult();
    this.goToConfirm(this.parseResult!);
  }

  private buildPaymentParseResult() {
    if (!this.parseResult) {
      this.parseResult = {
        intent: 'genera-pagamento',
        figlio: this.selectedStudente,
        figlioAutoSelected: this.studenti.length === 1,
        childMatch: this.selectedStudente ? { studente: this.selectedStudente, status: 'matched' } : null,
        data: null,
        importo: this.amountInput,
        confidence: 'full',
        tokens: [],
      };
    } else {
      this.parseResult.importo = this.amountInput;
      this.parseResult.intent = 'genera-pagamento';
      if (this.parseResult.figlio && this.parseResult.importo) {
        this.parseResult.confidence = 'full';
      }
    }
  }

  // --- Payment validation ---

  private runPaymentValidation() {
    const studente = this.selectedStudente;

    if (!studente) {
      this.canConfirm = false;
      return;
    }

    // Gate importo (sincrono): deve essere > 0 prima ancora di interrogare il backend.
    if (!this.parseResult?.importo || this.parseResult.importo <= 0) {
      this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.importoNonValido')];
      this.canConfirm = false;
      return;
    }

    // La configurazione mensa (stato manuale + tipo pagamento) NON è più nel DTO discovery:
    // si legge async da `getConfigurazione1` per-servizio. Vedi change
    // `riallinea-client-api-be-baseline`: `statoRicaricaManuale === ATTIVA` ⇒ `gestione_manuale`.
    this.loadingValidation = true;
    this.idServizioMensa().then(idServizio => {
      if (!idServizio) {
        this.loadingValidation = false;
        this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.erroreVerifica')];
        this.canConfirm = false;
        return;
      }
      this.configMensa.getConfigurazioneMensaCittadino({ gestioneMenseServizioId: idServizio }).pipe(
        takeUntil(this.destroy$),
      ).subscribe({
        next: (config) => {
          // Gate 1: la gestione manuale delle ricariche deve essere attiva.
          if (!config.gestione_manuale) {
            this.loadingValidation = false;
            this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.pagamentiNonDisponibili')];
            this.canConfirm = false;
            return;
          }

          // Gate 2: se REND_E_RICARICHE, verifica che non ci sia già un bollettino pendente.
          if (config.tipo_pagamento === ConfigTipoPagamentoEnum.PAGO_PA_RENDICONTAZIONE_E_RICARICHE) {
            // Il gate deve sapere solo "esiste UNA ricarica pendente?". Una ricarica IN_ATTESA_PAGAMENTO
            // non ha `dataPagamento`, quindi l'ordinamento la spinge in coda: senza `size` ampio potrebbe
            // cadere fuori dalla prima pagina (falso negativo → si genera un bollettino con uno già aperto).
            // `size` alto + sort secondario `data` (sempre valorizzato), allineato a pagamenti-mensa.
            this.studentiService.listRicaricheStudenteCittadino({
              idStudente: studente.id!, gestioneMenseServizioId: idServizio,
              pageable: { sort: ['dataPagamento,DESC', 'data,DESC'], size: 200 }
            }).pipe(
              map(response => response.content ?? []),
              takeUntil(this.destroy$),
              finalize(() => this.loadingValidation = false)
            ).subscribe({
              next: (ricariche: ServizioMensaRicaricaDTO[]) => {
                const hasPending = ricariche.some(r =>
                  r.stato_interno === ServizioMensaRicaricaStato.IN_ATTESA_PAGAMENTO
                );
                if (hasPending) {
                  this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.pagamentoInSospeso')];
                  this.canConfirm = false;
                } else {
                  this.confirmWarnings = [];
                  this.canConfirm = true;
                }
              },
              error: () => {
                this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.erroreVerifica')];
                this.canConfirm = false;
              }
            });
          } else {
            this.loadingValidation = false;
            this.confirmWarnings = [];
            this.canConfirm = true;
          }
        },
        error: () => {
          this.loadingValidation = false;
          this.confirmWarnings = [this.transloco.translate('shared.command_palette.validation.erroreVerifica')];
          this.canConfirm = false;
        }
      });
    });
  }

  // --- Payment execution ---

  async confirmSavePayment() {
    if (!this.canConfirm || this.confirmSaving) return;
    if (!this.selectedStudente?.id || !this.parseResult?.importo) return;

    const idServizio = await this.idServizioMensa();
    if (!idServizio) {
      this.toastNoServizio();
      return;
    }

    const studenteId = this.selectedStudente.id;
    // La `*2` stampiglia la data lato BE: niente `dataPagamento` nel form (parità pagamenti-mensa).
    const body: ServizioMensaAddPatchRicaricaForm = {
      valore: this.parseResult.importo,
    };

    this.confirmSaving = true;
    this.loaderService.showLoaderObserve$().pipe(
      switchMap(() => this.studentiService.addRicaricaStudenteCittadino({ idStudente: studenteId, gestioneMenseServizioId: idServizio, servizioMensaAddPatchRicaricaForm: body })),
      takeUntil(this.destroy$),
      finalize(() => {
        this.loaderService.hideLoader();
        this.confirmSaving = false;
      })
    ).subscribe({
      next: async () => {
        this.notificationService.openToast({ message: this.transloco.translate('shared.command_palette.toast.pagamentoGenerato'), color: 'success' });
        // Il saldo va ricaricato anche fuori da pagamenti-mensa (es. Home): emette il motivo
        // prima di navigare, così l'effetto centralizzato ricarica la discovery.
        this.globalService.emitMensaChanged('ricarica');
        await this.modalController.dismiss(null, ModalRoleEnum.CONFIRM);
        this.router.navigate([PathEnum.PAGES + PathEnum.PAGAMENTI_MENSA]);
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.openToast({
          message: error.error?.message || this.transloco.translate('shared.command_palette.toast.errorePagamento'),
          color: 'danger'
        });
      }
    });
  }

  // --- Navigation ---

  private async navigateToPayments() {
    await this.modalController.dismiss(null, ModalRoleEnum.CANCEL);
    this.router.navigate([PathEnum.PAGES + PathEnum.PAGAMENTI_MENSA]);
  }

  confirmModify() {
    if (this.isRemoveConfirm) {
      this.loadRemoveEvents();
      return;
    }
    if (this.isPaymentConfirm) {
      this.prepareAmountForm(this.parseResult?.importo);
      this.step = 'amount-form';
      return;
    }
    if (this.parseResult?.data) {
      this.prefilledDateInizio = format(this.parseResult.data.inizio, 'yyyy-MM-dd');
      this.prefilledDateFine = this.parseResult.data.fine
        ? format(this.parseResult.data.fine, 'yyyy-MM-dd')
        : this.prefilledDateInizio;
    }
    this.step = 'action-form';
  }

  // --- Remove-form step ---

  private loadRemoveEvents() {
    const studenteId = this.selectedStudente?.id;
    if (!studenteId) return;

    this.removeChips = [];
    this.selectedRemoveChip = null;
    this.loadingRemoveEvents = true;
    this.step = 'remove-form';

    const targetType = this.selectedAction?.actionType === 'rimuovi-assenza'
      ? ServizioMensaEventoCalendarioType.ASSENZA
      : ServizioMensaEventoCalendarioType.PASTA_IN_BIANCO;

    this.idServizioMensa().then(idServizio => {
      if (!idServizio) {
        this.loadingRemoveEvents = false;
        this.notificationService.openToast({
          message: this.transloco.translate('shared.command_palette.toast.erroreCaricamento'),
          color: 'danger'
        });
        this.goBackToSearch();
        return;
      }
      this.studentiService.listEventiCalendarioStudenteCittadino({ idStudente: studenteId, gestioneMenseServizioId: idServizio }).pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingRemoveEvents = false)
      ).subscribe({
        next: (eventi) => {
          const today = startOfDay(new Date());
        const futureEvents = eventi
          .filter(e => e.tipo === targetType && e.data && e.id)
          .filter(e => {
            const eventDate = parse(e.data!, 'dd/MM/yyyy', new Date());
            return isAfter(eventDate, today) || isEqual(eventDate, today);
          })
          .sort((a, b) => {
            const da = parse(a.data!, 'dd/MM/yyyy', new Date());
            const db = parse(b.data!, 'dd/MM/yyyy', new Date());
            return da.getTime() - db.getTime();
          })
          .slice(0, 6);

        if (futureEvents.length === 0) {
          const tipo = this.transloco.translate(this.selectedAction?.actionType === 'rimuovi-assenza'
            ? 'shared.command_palette.tipoEvento.assenza'
            : 'shared.command_palette.tipoEvento.pastaInBianco');
          const nome = this.selectedStudente?.nome ?? '';
          this.notificationService.openToast({
            message: this.transloco.translate('shared.command_palette.toast.nessunoFuturo', { tipo, nome }),
            color: 'warning'
          });
          this.goBackToSearch();
          return;
        }

          this.removeChips = futureEvents.map(e => this.buildRemoveChip(e));
        },
        error: () => {
          this.notificationService.openToast({
            message: this.transloco.translate('shared.command_palette.toast.erroreCaricamento'),
            color: 'danger'
          });
          this.goBackToSearch();
        }
      });
    });
  }

  private buildRemoveChip(evento: ServizioMensaEventoCalendarioDTO): RemoveChip {
    const eventDate = parse(evento.data!, 'dd/MM/yyyy', new Date());
    const today = startOfDay(new Date());
    const tomorrow = startOfDay(new Date());
    tomorrow.setDate(tomorrow.getDate() + 1);

    let label: string;
    if (isEqual(eventDate, today)) {
      label = this.transloco.translate('shared.command_palette.chip.oggi');
    } else if (isEqual(eventDate, tomorrow)) {
      label = this.transloco.translate('shared.command_palette.chip.domani');
    } else {
      const dayKeys = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
      label = this.transloco.translate('shared.command_palette.giorni.' + dayKeys[eventDate.getDay()]);
    }

    // Il locale date-fns è quello globale impostato da LanguageService.use().
    const shortDate = format(eventDate, 'd MMM');

    return { evento, label, shortDate };
  }

  selectRemoveChip(chip: RemoveChip) {
    this.selectedRemoveChip = chip;
  }

  get removeFormQuestion(): string {
    const nome = this.selectedStudente?.nome ?? '';
    const key = this.selectedAction?.actionType === 'rimuovi-assenza'
      ? 'shared.command_palette.removeQuestion.assenza'
      : 'shared.command_palette.removeQuestion.pasta';
    return this.transloco.translate(key, { nome });
  }

  get removeButtonLabel(): string {
    return this.transloco.translate(this.selectedAction?.actionType === 'rimuovi-assenza'
      ? 'shared.command_palette.actions.rimuoviAssenza'
      : 'shared.command_palette.actions.rimuoviPasta');
  }

  confirmRemoveFromForm() {
    if (!this.selectedRemoveChip || !this.selectedStudente?.id) return;

    const evento = this.selectedRemoveChip.evento;
    this.confirmRemoveEventId = evento.id!;

    // Build a parseResult-like state for the confirm step display
    const eventDate = parse(evento.data!, 'dd/MM/yyyy', new Date());
    this.parseResult = {
      intent: this.selectedAction!.actionType,
      figlio: this.selectedStudente,
      figlioAutoSelected: false,
      childMatch: { studente: this.selectedStudente!, status: 'matched' },
      data: { inizio: eventDate },
      importo: null,
      confidence: 'full',
      tokens: [],
    };

    this.confirmWarnings = [];
    this.canConfirm = true;
    this.loadingValidation = false;
    this.step = 'confirm';
  }

  // --- Confirm step (remove) ---

  async confirmRemove() {
    if (!this.confirmRemoveEventId || !this.selectedStudente?.id) return;
    if (this.confirmSaving) return;

    const idServizio = await this.idServizioMensa();
    if (!idServizio) {
      this.toastNoServizio();
      return;
    }

    this.confirmSaving = true;
    this.loaderService.showLoaderObserve$().pipe(
      switchMap(() => this.studentiService.removeEventoCalendarioStudenteCittadino({
        idStudente: this.selectedStudente!.id!,
        idEvento: this.confirmRemoveEventId!,
        gestioneMenseServizioId: idServizio,
      })),
      takeUntil(this.destroy$),
      finalize(() => {
        this.loaderService.hideLoader();
        this.confirmSaving = false;
      })
    ).subscribe({
      next: () => {
        const label = this.transloco.translate(this.selectedAction!.actionType === 'rimuovi-assenza'
          ? 'shared.command_palette.toast.assenzaRimossa'
          : 'shared.command_palette.toast.pastaRimossa');
        this.notificationService.openToast({ message: label, color: 'success' });
        const reason = this.classifyEventoReason(
          this.selectedAction!.actionType,
          this.parseResult!.data!.inizio,
          this.parseResult!.data!.fine,
        );
        this.globalService.emitMensaChanged(reason);
        this.modalController.dismiss(null, ModalRoleEnum.CONFIRM);
      },
      error: (error: HttpErrorResponse) => {
        this.notificationService.openToast({
          message: error.error?.message || this.transloco.translate('shared.command_palette.toast.erroreRimozione'),
          color: 'danger'
        });
      }
    });
  }

  get isRemoveConfirm(): boolean {
    return isRemoveAction(this.selectedAction?.actionType ?? null);
  }

  // --- Navigation ---

  goBackToSearch() {
    this.step = 'search';
    this.selectedAction = null;
    this.prefilledDateInizio = null;
    this.prefilledDateFine = null;
    this.amountInput = null;
    this.residuoHint = null;
    this.selectedStudente = this.studenti.length === 1 ? this.studenti[0] : null;
  }

  goBackToSelectChild() {
    this.step = 'select-child';
    this.selectedStudente = null;
  }

  onFormSaved(payload: { inizio: Date; fine: Date | null }) {
    const reason = this.classifyEventoReason(
      this.selectedAction?.actionType ?? null,
      payload.inizio,
      payload.fine,
    );
    this.globalService.emitMensaChanged(reason);
    this.modalController.dismiss(null, ModalRoleEnum.CONFIRM);
  }

  onFormBack() {
    if (this.studenti.length > 1) {
      this.goBackToSelectChild();
    } else {
      this.goBackToSearch();
    }
  }

  cancel() {
    this.modalController.dismiss(null, ModalRoleEnum.CANCEL);
  }

  // --- Token pill helpers ---

  getTokenColor(token: ParsedToken): string {
    if (token.status === 'fuzzy') return 'warning';
    if (token.type === 'figlio') return 'secondary';
    if (token.type === 'intent' || token.type === 'data' || token.type === 'importo') return 'success';
    return 'medium';
  }

  getTokenIcon(token: ParsedToken): string {
    switch (token.type) {
      case 'intent': return 'flash-outline';
      case 'figlio': return 'person-outline';
      case 'data': return 'calendar-outline';
      case 'importo': return 'card-outline';
      default: return 'help-outline';
    }
  }

  getTokenAriaLabel(token: ParsedToken): string {
    const typeLabel = this.tokenTypeLabel(token.type);
    const fuzzy = token.status === 'fuzzy'
      ? this.transloco.translate('shared.command_palette.announce.fuzzyCorretto', { raw: token.raw })
      : '';
    return this.transloco.translate('shared.command_palette.announce.tokenLineFuzzy', {
      tipo: typeLabel,
      valore: token.resolved,
      fuzzy,
    });
  }
}
